import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogOut, Coins, User, Mail, Phone, Calendar, Lock, History } from 'lucide-react';
import { authAPI } from '@/api/client';
import { toast } from 'sonner';
import PartialExamTab from '@/components/student/PartialExamTab';
import MockExamTab from '@/components/student/MockExamTab';
import MyBookingsTab from '@/components/student/MyBookingsTab';
import Footer from '@/components/Footer';
import NoticeModal from '@/components/student/NoticeModal';
import CreditHistoryModal from '@/components/student/CreditHistoryModal';

const StudentPortal = () => {
  const { user, credits, logout, refreshCredits, showExactValidityDate } = useAuth();
  const [activeTab, setActiveTab] = useState('partial');
  const [showProfile, setShowProfile] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    // Set page title
    document.title = 'Student Portal | Radiance Education';
    
    // Refresh credits on mount
    refreshCredits();
  }, []);

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Password changed successfully!');
      
      // Reset form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordChange(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-blue-50">
      {/* Notice Modal - Shows after login with delay */}
      <NoticeModal />
      
      {/* Mobile-First Responsive Header */}
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          {/* Top Row - Logo & User Info */}
          <div className="flex justify-between items-center py-3 sm:py-4">
            {/* Logo Only */}
            <div className="flex items-center">
              <img 
                src="https://radianceedu.com/wp-content/uploads/2025/11/Logo-Radiance-Education.png" 
                alt="Radiance Education" 
                className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto object-contain"
                data-testid="portal-logo"
              />
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-sm text-blue-100">
                Welcome, Dear <span className="font-semibold">{user?.name}</span>
                {user?.candidate_no && (
                  <span className="ml-2 text-xs text-blue-200">({user.candidate_no})</span>
                )}
                {user?.exam_category && (
                  <Badge className="ml-2 bg-white/20 backdrop-blur-lg">
                    {user.exam_category === 'PB' ? 'Paper Based' : 'Computer Delivered'}
                  </Badge>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowProfile(true)}
                className="hover:bg-white/30 text-white rounded-full bg-white/20 backdrop-blur-sm"
                title="View Profile"
              >
                <User className="w-6 h-6" />
              </Button>
              <Button 
                variant="outline" 
                onClick={logout} 
                className="bg-white/20 border-white/40 text-white hover:bg-white/30 text-sm"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Actions */}
            <div className="flex md:hidden items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowProfile(true)}
                className="hover:bg-white/30 text-white rounded-full h-9 w-9 bg-white/20 backdrop-blur-sm"
                title="Profile"
              >
                <User className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost"
                size="icon"
                onClick={logout} 
                className="hover:bg-white/20 text-white rounded-full h-9 w-9"
                title="Logout"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Mobile Welcome Message */}
          <div className="md:hidden pb-3 border-t border-white/20 pt-3">
            <div className="text-xs sm:text-sm text-center">
              Welcome, Dear <span className="font-semibold" data-testid="user-name">{user?.name}</span>
              {user?.candidate_no && (
                <span className="ml-1 text-xs text-blue-200">({user.candidate_no})</span>
              )}
              {user?.exam_category && (
                <Badge className="ml-2 bg-white/20 backdrop-blur-lg text-xs">
                  {user.exam_category === 'PB' ? 'Paper Based' : 'Computer Delivered'}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile Optimized */}
      <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Mobile-Responsive Tab Navigation */}
          <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 mb-4 sm:mb-6 md:mb-8 bg-white/80 backdrop-blur-lg shadow-lg h-auto p-1" data-testid="exam-tabs">
            <TabsTrigger 
              value="partial" 
              data-testid="partial-tab"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm md:text-base py-2 sm:py-2.5"
            >
              <span className="hidden sm:inline">Partial Exams</span>
              <span className="sm:hidden">Partial</span>
            </TabsTrigger>
            <TabsTrigger 
              value="mock" 
              data-testid="mock-tab"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm md:text-base py-2 sm:py-2.5"
            >
              <span className="hidden sm:inline">Mock Tests</span>
              <span className="sm:hidden">Mock</span>
            </TabsTrigger>
            <TabsTrigger 
              value="bookings" 
              data-testid="bookings-tab"
              className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs sm:text-sm md:text-base py-2 sm:py-2.5"
            >
              <span className="hidden sm:inline">My Bookings</span>
              <span className="sm:hidden">Bookings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="partial" data-testid="partial-tab-content">
            <PartialExamTab />
          </TabsContent>

          <TabsContent value="mock" data-testid="mock-tab-content">
            <MockExamTab />
          </TabsContent>

          <TabsContent value="bookings" data-testid="bookings-tab-content">
            <MyBookingsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Profile View Modal - Mobile Responsive */}
      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="max-w-md mx-3 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              My Profile
            </DialogTitle>
            <DialogDescription className="text-sm">
              View your account information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4 py-3 sm:py-4">
            <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardContent className="pt-4 sm:pt-6 space-y-3 sm:space-y-4 p-3 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm text-gray-600">Name</div>
                    <div className="font-semibold text-sm sm:text-base truncate">{user?.name}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm text-gray-600">Email</div>
                    <div className="font-semibold text-sm sm:text-base truncate">{user?.email}</div>
                  </div>
                </div>

                {user?.candidate_no && (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Badge className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 flex items-center justify-center bg-blue-600 text-white">#</Badge>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm text-gray-600">Candidate No</div>
                      <div className="font-semibold text-sm sm:text-base text-blue-600">{user?.candidate_no}</div>
                    </div>
                  </div>
                )}

                {user?.phone && (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm text-gray-600">Phone</div>
                      <div className="font-semibold text-sm sm:text-base truncate">{user?.phone}</div>
                    </div>
                  </div>
                )}
                
                {user?.created_at && (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm text-gray-600">Portal Validity</div>
                      <div className="font-semibold text-sm sm:text-base text-purple-600">
                        Till {(() => {
                          if (user.valid_until) {
                            const validDate = new Date(user.valid_until);
                            return validDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
                          } else {
                            const created = new Date(user.created_at);
                            const validTill = new Date(created);
                            validTill.setMonth(validTill.getMonth() + 5);
                            return validTill.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {user?.batch_code && (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm text-gray-600">Batch</div>
                      <Badge className="mt-1 text-xs">{user?.batch_code}</Badge>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 sm:gap-3 pt-2 sm:pt-3 border-t">
                  <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm text-gray-600">Available Credits</div>
                    <div className="font-bold text-blue-600 text-xl sm:text-2xl">{credits}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-xs text-center text-gray-500 pt-2">
              Contact admin to update your information
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <Button 
              variant="outline"
              onClick={() => {
                setShowProfile(false);
                setShowPasswordChange(true);
              }}
              className="flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
            >
              <Lock className="w-4 h-4" />
              <span>Change Password</span>
            </Button>
            <Button 
              onClick={() => setShowProfile(false)}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-6 h-6 text-blue-600" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new password
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                placeholder="Enter current password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder="Confirm new password"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Password Requirements:</strong>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>Minimum 6 characters</li>
                <li>Make sure both new passwords match</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button 
              variant="outline"
              onClick={() => {
                setShowPasswordChange(false);
                setPasswordData({
                  current_password: '',
                  new_password: '',
                  confirm_password: ''
                });
              }}
              disabled={changingPassword}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordChange}
              disabled={changingPassword}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credit History Modal */}
      <CreditHistoryModal 
        isOpen={showCreditHistory} 
        onClose={() => setShowCreditHistory(false)} 
      />

      {/* Credit History Link - Above Footer */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex justify-center">
          <Button
            variant="ghost"
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 flex items-center gap-2 font-bangla"
            onClick={() => setShowCreditHistory(true)}
          >
            <History className="h-4 w-4" />
            Credit History দেখুন
          </Button>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default StudentPortal;
